import { Component, Inject, OnDestroy } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { combineLatest, filter, map, Observable, shareReplay, Subject, takeUntil } from 'rxjs';

import { ApiImportPapersResult, ApiProjectDetails, ApiProjectInfo, ProjectDetailsClient, ProjectImportPapersClient, ProjectStatusEnum } from 'content-manager/src/app/core/open-api/content-manager-api-client';
import { IProjectReturnToEditorDialogData } from 'content-manager/src/app/projects/project/details/return-to-editor-dialog/project-return-to-editor-dialog-data';
import { ProjectReturnToEditorDialogComponent } from 'content-manager/src/app/projects/project/details/return-to-editor-dialog/project-return-to-editor-dialog.component';
import { ProjectStateService } from 'content-manager/src/app/projects/project/project-state.service';
import { ICMEnvironment } from 'content-manager/src/environments/environment.model';
import { DialogMessageService, ENV_TOKEN, FileResponse, Permission, PermissionService, ScConfirmationService } from 'sc-common';

@Component({
    templateUrl: './project-details.component.html',
    standalone: false
})
export class ProjectDetailsComponent implements OnDestroy {

    public readonly vm$: Observable<{
        detail: ApiProjectDetails;
        projectInfo: ApiProjectInfo;
        isManager: boolean;
        showActions: boolean;
        showImportPapers: boolean;
    }>;

    public isGeneratingReport = false;
    public downloadReport$: Observable<FileResponse>;

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private readonly _projectStateService: ProjectStateService,
        private readonly _projectImportPapersClient: ProjectImportPapersClient,
        private readonly _projectClient: ProjectDetailsClient,
        permissionService: PermissionService,
        private readonly _confirmationService: ScConfirmationService,
        private readonly _dialogMessageService: DialogMessageService,
        private readonly _dialogService: DialogService,
        @Inject(ENV_TOKEN) private readonly _env: ICMEnvironment) {

        const projectInfo$ = _projectStateService.model$;
        const detail$ = _projectStateService.switchMap(project => _projectClient.getDetails(project.id));
        const isManager$ = permissionService.memberPermissions$
            .pipe(
                map(permissions => permissions.includes(Permission.ContentManagerView))
            );

        this.vm$ = combineLatest({
            detail: detail$,
            projectInfo: projectInfo$,
            isManager: isManager$
        }).pipe(
            map(({ detail, projectInfo, isManager }) => ({
                detail,
                projectInfo,
                isManager,
                showActions: !projectInfo.isReadonly,
                showImportPapers: projectInfo.status === ProjectStatusEnum.Completed
            })),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.downloadReport$ = _projectStateService.firstSwitchMap(project => _projectClient.downloadReport(project.id));
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    public getTitleLink(titleIsbn: string): string {
        return `${ this._env.mainBaseUrl }/Admin/Title/Info?isbn=${ encodeURIComponent(titleIsbn) }`;
    }

    public applyChanges(): void {
        this._confirmationService.confirm({
            title: $localize`Apply Changes`,
            message: $localize`Are you sure you want to Apply Changes?`,
            acceptLabel: $localize`Yes`,
            rejectLabel: $localize`No`,
            accept$: this._projectStateService.firstSwitchMap(project => this._projectClient.complete(project.id)),
            acceptCallback: () => this._projectStateService.refreshState()
        });
    }

    public cancel(): void {
        this._confirmationService.confirm({
            title: $localize`Cancel Project`,
            message: $localize`Are you sure you want to Cancel Project?`,
            acceptLabel: $localize`Yes`,
            rejectLabel: $localize`No`,
            accept$: this._projectStateService.firstSwitchMap(project => this._projectClient.cancel(project.id)),
            acceptCallback: () => this._projectStateService.refreshState()
        });
    }

    public returnToEditors(): void {

        const dialogData: IProjectReturnToEditorDialogData = {
            returnAction: applyChanges => this._projectStateService.firstSwitchMap(project => this._projectClient.return(project.id, applyChanges))
        };

        const dialogRef = this._dialogService.open(ProjectReturnToEditorDialogComponent, {
            width: '30vw',
            header: $localize`Return to Editor(s)`,
            closable: true,
            data: dialogData
        });

        dialogRef.onClose
            .pipe(filter(result => !!result), takeUntil(this._destroy$))
            .subscribe(() => this._projectStateService.refreshState());
    }

    public generateReport(): void {
        this.isGeneratingReport = true;

        this._projectStateService.firstSwitchMap(project => this._projectClient.generateReport(project.id))
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => {
                    this._projectStateService.refreshState();
                    this.isGeneratingReport = false;
                },
                error: () => {
                    this.isGeneratingReport = false;
                }
            });
    }

    public importPapers(): void {
        this._confirmationService.confirm({
            title: $localize`Import Papers`,
            message: $localize`Are you sure you want to import papers?`,
            acceptLabel: $localize`Yes`,
            rejectLabel: $localize`No`,
            accept$: this._projectStateService.firstSwitchMap(project => this._projectImportPapersClient.importPapers(project.id)),
            acceptCallback: (result: ApiImportPapersResult) => {

                const validationMessages = result?.validationMessages ?? [];

                if (!validationMessages.length) {
                    this._dialogMessageService.showMessage({
                        header: $localize`Import Papers`,
                        message: $localize`Papers have been imported successfully`
                    });
                } else {
                    this._dialogMessageService.showMessages({
                        header: $localize`Import Papers`,
                        messages: validationMessages
                    });
                }

                this._projectStateService.refreshState();
            }
        });
    }
}
